import * as React from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/auth/use-auth';

const Schema = z.object({
  username: z.string().min(1, 'Required'),
  password: z.string().min(1, 'Required'),
});
type FormVals = z.infer<typeof Schema>;

export const Route = createFileRoute('/login')({
  beforeLoad: ({ context }) => {
    if ((context as any).user) throw redirect({ to: '/' });
  },
  component: LoginPage,
});

function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [err, setErr] = React.useState<string | null>(null);
  const f = useForm<FormVals>({
    resolver: zodResolver(Schema),
    defaultValues: { username: '', password: '' },
  });

  async function onSubmit(v: FormVals) {
    setErr(null);
    try {
      await login(v.username, v.password);
      // Let React flush the auth state update (setQueryData → re-render → new
      // RouterProvider context) before triggering the client-side navigation so
      // _app's beforeLoad sees context.user !== null.
      await new Promise((r) => setTimeout(r, 0));
      await nav({ to: '/' });
    } catch (e: any) {
      setErr(e?.message ?? 'Login failed');
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader><CardTitle>DALEEP NS Dashboard</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={f.handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="username">Username</Label>
              <Input id="username" autoComplete="username" {...f.register('username')} />
              {f.formState.errors.username && (
                <p className="text-xs text-destructive">{f.formState.errors.username.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" autoComplete="current-password" {...f.register('password')} />
              {f.formState.errors.password && (
                <p className="text-xs text-destructive">{f.formState.errors.password.message}</p>
              )}
            </div>
            {err && <p data-testid="login-error" className="text-sm text-destructive">{err}</p>}
            <Button type="submit" className="w-full" disabled={f.formState.isSubmitting}>
              {f.formState.isSubmitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
